import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { webAppUrl } from '../utils/webAppUrl';
import { Mail, MapPin, Phone } from 'lucide-react';
import deeplabsLogo from '../assets/images/deeplabs_logo.webp';
import gnbLogo from '../assets/images/GNB_logo.svg';
import { LocalizedLink } from '../i18n/LocalizedLink';

const Footer: React.FC = () => {
  const { t } = useTranslation('common');

  useEffect(() => {
    (window as any).onPopKBAuthMark = function () {
      window.open('', 'KB_AUTHMARK', 'height=604, width=648, status=yes, toolbar=no, menubar=no, location=no');
      const form = document.getElementById('KB_AUTHMARK_FORM') as HTMLFormElement;
      if (form) {
        form.action = 'https://okbfex.kbstar.com/quics';
        form.target = 'KB_AUTHMARK';
        form.submit();
      }
    };
  }, []);

  const handleKBAuthMarkClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if ((window as any).onPopKBAuthMark) {
      (window as any).onPopKBAuthMark();
    }
  };

  const brandDesc = t('footer.brandDesc').split('\n');

  return (
    <footer className="main_footer">
      <div className="footer_container">
        <div className="footer_content">
          <div className="footer_brand_column">
            <LocalizedLink to="/" className="footer_brand_logo">
              <img src={gnbLogo} alt={t('footer.logoAlt')} className="footer_logo_img" loading="lazy" />
            </LocalizedLink>
            <p className="footer_brand_desc">
              {brandDesc.map((line, i) => (
                <React.Fragment key={i}>
                  {i > 0 ? <br /> : null}
                  {line}
                </React.Fragment>
              ))}
            </p>
            <div className="footer_brand_contacts">
              <a href="mailto:contact@thedeeplabs.com" className="footer_contact_line">
                <Mail size={14} strokeWidth={1.8} aria-hidden />
                <span>contact@thedeeplabs.com</span>
              </a>
              <p className="footer_contact_line">
                <Phone size={14} strokeWidth={1.8} aria-hidden />
                <span>{t('footer.phone')}</span>
              </p>
              <p className="footer_contact_line">
                <MapPin size={14} strokeWidth={1.8} aria-hidden />
                <span>{t('footer.address')}</span>
              </p>
            </div>
          </div>

          <div className="footer_menu_columns">
            <div className="footer_menu_column">
              <h4 className="footer_menu_title">{t('footer.service')}</h4>
              <a href={webAppUrl('/guide')} className="footer_menu_link">
                {t('footer.serviceIntro')}
              </a>
              <LocalizedLink to="/" className="footer_menu_link">
                {t('footer.guide')}
              </LocalizedLink>
              <LocalizedLink to="/service/pricing" className="footer_menu_link">
                {t('footer.pricing')}
              </LocalizedLink>
              <LocalizedLink to="/blog/faq" className="footer_menu_link">
                {t('footer.faq')}
              </LocalizedLink>
            </div>

            <div className="footer_menu_column">
              <h4 className="footer_menu_title">{t('footer.community')}</h4>
              <a href={webAppUrl('/notice')} className="footer_menu_link">
                {t('footer.notice')}
              </a>
              <a
                href="http://pf.kakao.com/_BvxdCX"
                target="_blank"
                rel="noopener noreferrer"
                className="footer_menu_link"
              >
                {t('footer.kakao')}
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="footer_menu_link"
              >
                {t('footer.instagram')}
              </a>
            </div>

            <div className="footer_menu_column">
              <h4 className="footer_menu_title">{t('footer.legal')}</h4>
              <LocalizedLink to="/terms" className="footer_menu_link">
                {t('footer.terms')}
              </LocalizedLink>
              <LocalizedLink to="/privacy" className="footer_menu_link">
                {t('footer.privacy')}
              </LocalizedLink>
              <LocalizedLink to="/cookie-policy" className="footer_menu_link">
                {t('footer.cookie')}
              </LocalizedLink>
              <LocalizedLink to="/youth" className="footer_menu_link">
                {t('footer.youth')}
              </LocalizedLink>
            </div>
          </div>
        </div>

        <div className="footer_bottom">
          <div className="footer_bottom_left">
            <div className="footer_copyright">Copyright © 2025 DeepLabs all rights reserved</div>
            <div className="footer_logo">
              <a href="https://thedeeplabs.com" target="_blank" rel="noopener noreferrer">
                <img src={deeplabsLogo} alt="DeepLabs" className="footer_logo_img" loading="lazy" />
              </a>
            </div>
          </div>

          <div className="footer_business_info">
            <div className="footer_business_row">
              <span>{t('footer.ceo')}</span>
              <span className="footer_divider">|</span>
              <span>{t('footer.businessNumber')} : 116-81-80216</span>
              <span className="footer_divider">|</span>
              <span>{t('footer.mailOrder')} : 제 2025-서울광진-1541 호</span>
            </div>
            <div className="footer_business_row">
              <span>{t('footer.fullAddress')}</span>
              <span className="footer_divider">|</span>
              <span>{t('footer.hosting')}</span>
              <span className="footer_divider">|</span>
              <span>{t('footer.support')}</span>
            </div>
          </div>

          <div className="footer_auth_mark">
            <form id="KB_AUTHMARK_FORM" method="get" style={{ display: 'none' }}>
              <input type="hidden" name="page" value="C021590" />
              <input type="hidden" name="cc" value="b034066:b035526" />
              <input type="hidden" name="mHValue" value="c9b3a616eb8b65b42c2c85ed6807d754" />
            </form>
            <button
              type="button"
              onClick={handleKBAuthMarkClick}
              style={{ display: 'inline-block', border: 0, background: 'transparent', padding: 0 }}
            >
              <img
                src="https://img1.kbstar.com/img/escrow/escrowcmark.gif"
                alt={t('footer.kbAuth')}
                style={{ border: 0, maxWidth: '60px', height: 'auto' }} loading="lazy" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
